import { TableCell } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppToolbar, DatePickerComponent, DatePickerWrapper, MAX_DATE, MIN_DATE } from "../components/AppToolbar";
import ReportTable from "../components/ReportTable";
import { ReportHeader } from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { useMaxStoredDate } from "../hooks/useStoredDate";
import AssignationProjectionReport from "../models/AssignationProjectionReport";
import AssignationService from "../services/AssignationService";
import { RequestStatus } from "../types/RequestStatus";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { Typography } from "@mui/material";
import { filtersBarStyle, Token } from "../theme";

export default function AssignationProjectionList() {
  const { t } = useTranslation();

  const [selectedDate, setSelectedDate] = useMaxStoredDate();
  const [requestStatus, setRequestStatus] = useState<RequestStatus | undefined>(undefined);

  const [headers, setHeaders] = useState<ReportHeader[]>();
  const [rows, setRows] = useState<AssignationProjectionReport[]>([]);
  const [headersBonus, setHeadersBonus] = useState<ReportHeader[]>();
  const [rowsBonus, setRowsBonus] = useState<AssignationProjectionReport[]>([]);

  const searchParams = new URLSearchParams(useLocation().search);
  const month = searchParams.get("month");
  const year = searchParams.get("year");
  const { updateUrl } = useUpdateUrl(Routes.ASSIGNATION_PROJECTION, searchParams);

  useEffect(() => {
    initAssignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    const date = new Date();

    if (month !== null) {
      date.setMonth(parseInt(month));
      setSelectedDate(date);
    } else {
      searchParams.set("month", date.getMonth().toString());
    }

    if (year !== null) {
      date.setFullYear(parseInt(year));
      setSelectedDate(date);
    } else {
      searchParams.set("year", date.getFullYear().toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initAssignations = async () => {
    try {
      setRequestStatus(RequestStatus.LOADING);

      // Find normal and bonus assignations
      const responses = await Promise.all([
        AssignationService.getAssignationProjection(
          selectedDate ? selectedDate.getFullYear() : new Date().getFullYear(),
          selectedDate ? selectedDate.getMonth() : new Date().getMonth(),
          false,
        ),
        AssignationService.getAssignationProjection(
          selectedDate ? selectedDate.getFullYear() : new Date().getFullYear(),
          selectedDate ? selectedDate.getMonth() : new Date().getMonth(),
          true,
        ),
      ]);

      const [assignationProjections, bonusProjections] = responses;
      const assignations = assignationProjections.getContentOrThrowError();
      const bonus = bonusProjections.getContentOrThrowError();

      setHeaders(assignations.headers);
      setRows(assignations.body);
      setHeadersBonus(bonus.headers);
      setRowsBonus(bonus.body);

      setRequestStatus(RequestStatus.SUCCESS);
    } catch (error) {
      setRequestStatus(RequestStatus.ERROR);
      NotificationService.error("Error getting assignment projections");
    }
  };

  return requestStatus === RequestStatus.ERROR ? (
    <></>
  ) : (
    <AppContainerFullHeight
      topSection={
        <AppToolbar title={t("Assignation Projection")}>
          <DatePickerWrapper style={filtersBarStyle()}>
            <DatePickerComponent
              views="month"
              label="Month"
              value={selectedDate}
              inputFormat="MMMM"
              onChange={(selectedDate) => {
                setSelectedDate(selectedDate!);
                searchParams.set("month", selectedDate!.getMonth().toString());
                updateUrl(Routes.ASSIGNATION_PROJECTION, searchParams);
              }}
            />
            <DatePickerComponent
              views="year"
              label="Year"
              value={selectedDate}
              inputFormat="yyyy"
              onChange={(selectedDate) => {
                setSelectedDate(selectedDate!);
                searchParams.set("year", selectedDate!.getFullYear().toString());
                updateUrl(Routes.ASSIGNATION_PROJECTION, searchParams);
              }}
              maxDate={MAX_DATE}
              minDate={MIN_DATE}
            />
          </DatePickerWrapper>
        </AppToolbar>
      }
      bottomSection={
        <>
          <Typography variant="h6" style={{ color: Token.textPrimary, padding: "0 0 0.5em 0", fontWeight: "bold" }}>
            Assigned
          </Typography>
          <ReportTable<AssignationProjectionReport>
            stickyHeader
            maxHeight="335px"
            headers={headers!}
            rows={rows}
            tableRow={(instance) => (
              <>
                <TableCell style={instance.projectName === "TOTAL" ? { fontWeight: "bold" } : undefined}>{instance.projectName}</TableCell>

                {instance.totalAssignations &&
                  instance.totalAssignations.map((v, index) => <TableCell key={`${instance.projectName}-${index}`}>{v}</TableCell>)}
              </>
            )}
          />
          <Typography variant="h6" style={{ color: Token.textPrimary, padding: "0 0 0.5em 0", fontWeight: "bold", margin: "20px 0 0 0" }}>
            Full or Partial Bonused
          </Typography>
          <ReportTable<AssignationProjectionReport>
            stickyHeader
            maxHeight="335px"
            headers={headersBonus!}
            rows={rowsBonus}
            tableRow={(instance) => (
              <>
                <TableCell style={instance.projectName === "TOTAL" ? { fontWeight: "bold" } : undefined}>{instance.projectName}</TableCell>

                {instance.totalAssignations &&
                  instance.totalAssignations.map((v, index) => <TableCell key={`${instance.projectName}-${index}`}>{v}</TableCell>)}
              </>
            )}
          />
        </>
      }
    />
  );
}
